import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { Paper, Typography, Box, Chip, Rating } from '@mui/material';

const containerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '600px',
  borderRadius: '16px',
  overflow: 'hidden'
};

const Map = ({ center, businesses, selectedBusiness, activeMarker, setActiveMarker, searchLocation }) => {
  const [mapRef, setMapRef] = useState(null);

  // Load Google Maps API
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  });

  // Function to handle map load and store reference
  const onMapLoad = useCallback((map) => {
    setMapRef(map);
  }, []);

  // Function to fit bounds to include all markers when map is loaded
  const fitBoundsToMarkers = useCallback(() => {
    if (!mapRef || !businesses || businesses.length === 0) return;
    
    const bounds = new window.google.maps.LatLngBounds();
    
    // Add all business locations to bounds
    businesses.forEach(business => {
      bounds.extend({
        lat: business.geometry.location.lat(),
        lng: business.geometry.location.lng()
      });
    });
    
    // Add search location to bounds if available
    if (searchLocation) {
      bounds.extend({
        lat: searchLocation.lat,
        lng: searchLocation.lng
      });
    }
    
    // Fit the map to the bounds
    mapRef.fitBounds(bounds);
    
    // If there's only one marker, zoom out a bit
    if (businesses.length === 1) {
      const currentZoom = mapRef.getZoom();
      mapRef.setZoom(currentZoom > 14 ? 14 : currentZoom);
    }
  }, [mapRef, businesses, searchLocation]);

  // Use effect to fit bounds when map and businesses are available
  useEffect(() => {
    if (mapRef && businesses && businesses.length > 0) {
      fitBoundsToMarkers();
    }
  }, [mapRef, businesses, fitBoundsToMarkers]);

  // Render loading state if Google Maps API is not loaded yet
  if (!isLoaded) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading map...</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', height: '100%' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={13}
        onLoad={onMapLoad}
        options={{
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        }}
      >
        {/* Search Location Marker */}
        {searchLocation && (
          <Marker
            position={{ lat: searchLocation.lat, lng: searchLocation.lng }}
            icon={{
              path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
              fillColor: '#FF5252',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              scale: 2,
              anchor: new window.google.maps.Point(12, 22),
            }}
          >
            {activeMarker === 'search-location' && (
              <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                <div>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Search Location
                  </Typography>
                  <Typography variant="body2">{searchLocation.address}</Typography>
                </div>
              </InfoWindow>
            )}
          </Marker>
        )}

        {/* Business Markers */}
        {businesses.map((business, index) => {
          const position = {
            lat: business.geometry.location.lat(),
            lng: business.geometry.location.lng()
          };
          
          return (
            <Marker
              key={business.place_id}
              position={position}
              onClick={() => setActiveMarker(business)}
              label={{
                text: `${index + 1}`,
                color: '#ffffff',
                fontWeight: 'bold',
                fontSize: business.isTarget ? '16px' : '14px'
              }}
              icon={{
                path: business.isTarget ? 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z' : window.google.maps.SymbolPath.CIRCLE,
                fillColor: business.isTarget ? '#4568dc' : 
                          (selectedBusiness?.place_id === business.place_id ? '#4caf50' : 
                           index < 3 ? '#4caf50' : // Green for top 3
                           index >= 3 && index < 10 ? '#ff9800' : // Warning color for 4-10
                           '#f44336'), // Red for the rest
                fillOpacity: selectedBusiness?.place_id === business.place_id ? 1 : 0.8,
                strokeColor: selectedBusiness?.place_id === business.place_id ? '#ffffff' : '#666666',
                strokeWeight: selectedBusiness?.place_id === business.place_id ? 3 : 1,
                scale: business.isTarget ? 2 : (selectedBusiness?.place_id === business.place_id ? 20 : 15),
                anchor: business.isTarget ? new window.google.maps.Point(12, 22) : undefined,
                labelOrigin: business.isTarget ? new window.google.maps.Point(12, 10) : undefined
              }}
            >
              {activeMarker === business && (
                <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                  <Box sx={{ minWidth: 200 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ flex: 1 }}>
                        {business.name}
                      </Typography>
                      <Chip 
                        label={`Rank #${businesses.findIndex(b => b.place_id === business.place_id) + 1}`}
                        size="small"
                        color={business.isTarget ? 'primary' : 'default'}
                        sx={{ 
                          height: 24,
                          minWidth: 70,
                          backgroundColor: business.isTarget ? '#4568dc' : 
                            (index < 3 ? ['#FFD700', '#C0C0C0', '#CD7F32'][index] : undefined),
                          color: business.isTarget || index < 3 ? '#ffffff' : undefined,
                          fontWeight: 'bold'
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {business.vicinity}
                    </Typography>
                    {business.rating && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Rating
                          value={business.rating}
                          readOnly
                          size="small"
                          precision={0.1}
                        />
                        <Typography variant="body2" color="text.secondary">
                          ({business.user_ratings_total})
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </InfoWindow>
              )}
            </Marker>
          );
        })}
      </GoogleMap>
    </Paper>
  );
};

export default Map;
